import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import AdminLayout from "../../layout/AdminLayout";
import { useAuth } from "../../../context/AuthContext";
import * as model from "../../../models/SkillsModel";
import { useNotificationCount } from "../../../context/NotificationCountContext";
import SkillTable from "./SkillTable";
import { ConfirmDelete } from "../../modals/ConfirmDelete";
import { apiError } from "../../../helpers/ErrorHelper";
import { toast } from "react-toastify";
import { SkillForm } from "./SkillForm";
import PageHeader from "../../common/PageHeader";
import config from "../../../config/config";
import { checkPermission } from "../../../helpers/GeneralHelper";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dateFormat from "dateformat";

export default function Skills() {
  const module = {
    title: 'Skills',
    singularTitle: 'Skill',
    key: 'skills',
    route: 'skills',
    primaryCol: '_skill_id'
  };

  const { state } = useAuth();
  let user_data = state.user_data;

  const navigate = useNavigate();

  const { notificationCount, setNotificationCount } = useNotificationCount();

  const [modalKey, setModalKey] = useState(0);

  const [toggleAddModal, setToggleAddModal] = useState(false);

  // Update Start
  const [toggleUpdateModal, setToggleUpdateModal] = useState(false);
  const [singleData, setSingleData] = useState({});
  const openUpdateModal = (data) => {
    setSingleData(data);
    setModalKey((prevKey) => prevKey + 1);
    setToggleUpdateModal(true);
  };
  // Update End

  // Delete Start
  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
  const openDeleteModal = (data) => {
    setSingleData(data);
    setToggleDeleteModal(true);
  };

  const deleteData = async() => {
    setLoading(true);

    try {
      const response = await model.remove(user_data.user_token, singleData[module.primaryCol]);
      if (response.status) {
        setNotificationCount(localStorage.getItem('unread_notification_count'));
        toast.success(response.message);

        model.saveToStorage(response.data[module.key], response.data.pagination);
        refreshData(); 
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);

      if (error.response?.data.invalid_token == 1) {
        navigate('/re-login');
      }
    }

    setToggleDeleteModal(false);
    setLoading(false);
  }
  // Delete End

  // Flush Start
  const flashData = async () => {
    localStorage.removeItem(module.key);
    fetchData(limit, page);
  };
  // Flush End

  // List Start
  const [listData, setListData] = useState([]);
  const [cacheTime, setCacheTime] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isTrash, setIsTrash] = useState(false);

  const fetchTrash = (isTrash) => {
    setIsTrash(isTrash);
  }

  const fetchData = async (newLimit, page) => {
    setLoading(true);

    try {
      let apiParam = {
        page: page,
        limit: newLimit,
        user_token: user_data.user_token,
        isTrash: isTrash ? 1 : 0,
      };
      const response = await model.getData(apiParam);
      if (typeof response == typeof undefined) {
        navigate("/re-login");
        return;
      }

      setListData(response[module.key]);
      setTotalRows(response.pagination.total_rows);
      setLimit(newLimit);
      setPage(page);

      setNotificationCount(localStorage.getItem("unread_notification_count"));

      if (response.time) {
        setCacheTime(response.time);
      }

      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };
  // List End

  const refreshData = async () => {
    fetchData(limit, page);
  }

  const exportToExcel = async () => {
    if (!listData || listData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    // Generate timestamp (YYYYMMDD_HHMMSS + random 4 digits)
    const timestamp = new Date().toISOString()
                                .replace(/[-:T]/g, "") // remove special chars
                                .split(".")[0];        // remove milliseconds

    const fileName = `${module.title}_${timestamp}.xlsx`;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`${module.title}`);

    // Define headers and style
    const headers = [
      { header: "Skill Name", key: "skill_name" },
      { header: "Added On", key: "skill_create_time" },
      { header: "Last Edited On", key: "skill_update_time" },
    ];

    //worksheet.columns = headers;
    worksheet.columns = [
      { header: "Skill Name", key: "skill_name" },
      { header: "Added On", key: "skill_create_time" },
      { header: "Last Edited On", key: "skill_update_time" },
    ];

    // Add rows
    listData.forEach(row => {
      worksheet.addRow({
        skill_name: row.skill_name,
        skill_create_time: row.skill_create_time && row.skill_create_time != '0000-00-00' ? dateFormat(row.skill_create_time, 'dd/mm/yyyy') : '',
        skill_update_time: row.skill_update_time && row.skill_update_time != '0000-00-00' ? dateFormat(row.skill_update_time, 'dd/mm/yyyy') : '',
      });
    });

    // Style header
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'D9E1F2' }, // Light blue
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Generate Excel file and save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>{module.title} - Digital CMS</title>
      </Helmet>

      <div className="main-content app-content">
        <div className="container-fluid">
          <PageHeader module={module} isTrash={isTrash} toggleTrash={fetchTrash} />

          {/* <!-- Start::row-1 --> */}
          <div className="row">
            <div className="col-xl-12">
              <div className="card custom-card">
                {! isTrash && <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div className="card-title">{ config.isCache ? `Last Synced On ${cacheTime}` : module.title }</div>

                  <div className="d-flex flex-wrap gap-2">
                    {listData.length > 0 && (
                      // <div className="col-xl-6 mt-4 mx-2">
                        <button className="btn btn-primary ms-3" onClick={exportToExcel}>Download Excel</button>
                      // </div>
                    )}
                    {checkPermission(module.route, 'add') && 
                      <button className="btn btn-primary btn-sm" onClick={() => setToggleAddModal(true)}>
                        <i className="ri-add-line me-1 fw-semibold align-middle"></i>
                        Add
                      </button>
                    }

                    {config.isCache ? 
                      <button className="btn btn-success-light btn-sm" onClick={() => flashData()}>
                        <i className="ri-refresh-line me-1 fw-semibold align-middle"></i> Refresh
                      </button> : ''
                    }
                  </div>
                </div>}
                <div className="card-body">
                  <div className="table-responsive">
                    <SkillTable
                      module={module}
                      data={listData}
                      loading={loading}
                      totalRows={totalRows}
                      page={page}
                      limit={limit}
                      fetchData={fetchData}
                      openUpdateModal={openUpdateModal}
                      openDeleteModal={openDeleteModal}
                      isTrash={isTrash}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* <!--End::row-1 --> */}

          {/* <!--Add Modal Start--> */}
          <SkillForm
            module={module}
            showModal={toggleAddModal}
            setShowModal={setToggleAddModal}
            refreshData={refreshData}
          />
          {/* <!--Add Modal End--> */}

          {/* <!--Update Modal Start--> */}
          <SkillForm
            module={module}
            showModal={toggleUpdateModal}
            setShowModal={setToggleUpdateModal}
            refreshData={refreshData}
            data={singleData}
            modalKey={modalKey}
          />
          {/* <!--Update Modal End--> */}

          {/* <!--Delete Modal Start--> */}
          <ConfirmDelete
            module={module}
            showModal={toggleDeleteModal}
            setShowModal={setToggleDeleteModal}
            loading={loading}
            onConfirm={deleteData}
          />
          {/* <!--Delete Modal End--> */}
        </div>
      </div>

      <Helmet></Helmet>
    </AdminLayout>
  );
}
